import StencilHTMLElement from "./stencil-element";

class CartDrawer extends StencilHTMLElement {
  name = "CartDrawer";
  lineItems;

  /** @type {Promise<void>} Survives DOM replacement so repeat adds serialize correctly. */
  static _sectionRefreshChain = Promise.resolve();

  /** Ensures upsell / other Swipers in the mini-cart re-measure after open (init often runs while offcanvas is display:none). */
  static _offcanvasSwiperNudgeBound = false;

  static get refreshLogPrefix() {
    return "[cart-drawer]";
  }

  static ensureOffcanvasSwiperNudge() {
    if (CartDrawer._offcanvasSwiperNudgeBound || typeof document === "undefined") {
      return;
    }
    const panel = document.getElementById("cart-offcanvas");
    if (!panel) {
      return;
    }
    CartDrawer._offcanvasSwiperNudgeBound = true;
    panel.addEventListener("shown.bs.offcanvas", () => {
      document.querySelectorAll("#cart-offcanvas-content swiper-slider").forEach((/** @type {HTMLElement & { instance?: import("swiper").Swiper }} */ el) => {
        const inst = el.instance;
        if (!inst) {
          return;
        }
        try {
          inst.slideTo(0, 0);
          inst.update();
        } catch {
          /* Swiper update failed — non-fatal */
        }
      });
    });
  }

  /**
   * @param {Element | null | undefined} drawerRoot `#cart-offcanvas-content`
   */
  static nudgeSwipersInDrawer(drawerRoot) {
    if (!drawerRoot) {
      return;
    }
    const run = (/** @type {HTMLElement & { instance?: import("swiper").Swiper }} */ el) => {
      const inst = el.instance;
      if (!inst) {
        return;
      }
      try {
        inst.slideTo(0, 0);
        inst.update();
      } catch {
        /* ignore */
      }
    };
    drawerRoot.querySelectorAll("swiper-slider").forEach((el) => {
      if (el.instance) {
        requestAnimationFrame(() => run(/** @type {any} */ (el)));
      } else {
        el.addEventListener("swiper-slider:ready", () => run(/** @type {any} */ (el)), { once: true });
      }
    });
  }

  /**
   * Full Swiper teardown + re-init on each `swiper-slider` under the drawer.
   * `update()` / `slideTo` are not enough if slides or external nav bindings were wrong at first init
   * (e.g. after a minicart HTML swap while offcanvas was hidden).
   *
   * @param {Element | null | undefined} drawerRoot
   */
  static async rebuildSwipersInDrawer(drawerRoot) {
    if (!drawerRoot) {
      return;
    }
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    const sliders = [...drawerRoot.querySelectorAll("swiper-slider")];
    for (const el of sliders) {
      const htmlEl = /** @type {HTMLElement & { instance?: import("swiper").Swiper; init?: () => Promise<void> }} */ (
        el
      );
      if (htmlEl.instance) {
        try {
          htmlEl.instance.destroy(true, true);
        } catch {
          /* ignore */
        }
        htmlEl.instance = undefined;
      }
      if (typeof htmlEl.init === "function") {
        try {
          await htmlEl.init();
        } catch {
          /* ignore */
        }
      }
    }
  }

  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
    this.lineItems = this.querySelectorAll("card-line-item");
  }

  init() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    //const removeButtons = this.querySelectorAll('.cart-drawer-line-item-remove');
    //this.addEventListener('click', this.onCartDrawerClick.bind(this));
  }

  /**
   * Queue a section refresh (same entry point from JS or from <cart-drawer> in the DOM).
   *
   * @param {{
   *   open?: boolean;
   *   offcanvas?: { show?: () => void } | null;
   *   cartAjaxResponse?: { sections?: Record<string, string> } | null;
   * }} [options]
   */
  async refreshFromSection(options = {}) {
    return CartDrawer.queueSectionRefresh(options);
  }

  /**
   * @param {{
   *   open?: boolean;
   *   offcanvas?: { show?: () => void } | null;
   *   cartAjaxResponse?: { sections?: Record<string, string> } | null;
   * }} [options]
   */
  static async queueSectionRefresh(options = {}) {
    CartDrawer._sectionRefreshChain = CartDrawer._sectionRefreshChain
      .catch(() => {
        /* prior refresh failed — continue chain */
      })
      .then(() => CartDrawer._performSectionRefresh(options));
    await CartDrawer._sectionRefreshChain;
  }

  /**
   * @param {{
   *   open?: boolean;
   *   offcanvas?: { show?: () => void } | null;
   *   cartAjaxResponse?: { sections?: Record<string, string> } | null;
   * }} [options]
   */
  /**
   * Prefer cart URL for Section Rendering requests so `cart` in Liquid matches the session after AJAX
   * add. `location.pathname` (e.g. a product page) can yield stale Upsell / `cart.items | last` in edge cases.
   * @see https://shopify.dev/docs/api/ajax/reference/cart#bundled-section-rendering (`sections_url`)
   */
  static sectionRenderFetchBase() {
    if (typeof window === "undefined") {
      return "/";
    }
    const cartUrl = window.routes?.cart_url;
    if (typeof cartUrl === "string" && cartUrl.startsWith("/")) {
      return cartUrl;
    }
    return window.location.pathname || "/";
  }

  static async _performSectionRefresh(options = {}) {
    const { open = true, offcanvas = null, cartAjaxResponse = null } = options;
    CartDrawer.ensureOffcanvasSwiperNudge();

    const sectionWrapper = document.querySelector(".section-offcanvas-cart");
    if (!sectionWrapper?.id) {
      console.warn(CartDrawer.refreshLogPrefix, "_performSectionRefresh: .section-offcanvas-cart not found");
      return;
    }
    const sectionId = sectionWrapper.id.replace("shopify-section-", "");

    try {
      let sectionHtml = null;
      const sections = cartAjaxResponse?.sections;
      if (sections && typeof sections === "object") {
        if (typeof sections[sectionId] === "string") {
          sectionHtml = sections[sectionId];
        } else {
          const entries = Object.entries(sections);
          if (entries.length === 1 && typeof entries[0][1] === "string") {
            sectionHtml = entries[0][1];
          }
        }
      }

      const fetchSectionText = async (base) => {
        const joiner = base.includes("?") ? "&" : "?";
        const fetchUrl = `${base}${joiner}section_id=${encodeURIComponent(sectionId)}&_cb=${Date.now()}`;
        const response = await fetch(fetchUrl, { cache: "no-store" });
        return response.text();
      };

      if (!sectionHtml) {
        const primaryBase = CartDrawer.sectionRenderFetchBase();
        sectionHtml = await fetchSectionText(primaryBase);
      }

      let parsedHtml = new DOMParser().parseFromString(sectionHtml, "text/html");
      let refreshedElement = parsedHtml.querySelector("#cart-offcanvas-content");

      // `/cart?section_id=…` sometimes omits overlay markup; without a swap we return early and never
      // call Offcanvas.show(). Retry with the current page URL so the drawer still opens.
      if (!refreshedElement) {
        const fallbackBase = typeof location !== "undefined" ? location.pathname || "/" : "/";
        if (fallbackBase !== CartDrawer.sectionRenderFetchBase()) {
          sectionHtml = await fetchSectionText(fallbackBase);
          parsedHtml = new DOMParser().parseFromString(sectionHtml, "text/html");
          refreshedElement = parsedHtml.querySelector("#cart-offcanvas-content");
        }
      }

      if (!refreshedElement) {
        console.warn(
          CartDrawer.refreshLogPrefix,
          "_performSectionRefresh: #cart-offcanvas-content missing in section response"
        );
        return;
      }

      const applyDomUpdate = () => {
        const live = document.querySelector("#cart-offcanvas-content");
        if (live) {
          live.outerHTML = refreshedElement.outerHTML;
        }
      };

      if (typeof document.startViewTransition === "function") {
        try {
          const vt = document.startViewTransition(applyDomUpdate);
          if (vt?.finished) {
            await vt.finished;
          }
        } catch {
          applyDomUpdate();
        }
      } else {
        applyDomUpdate();
      }

      const liveDrawer = document.querySelector("#cart-offcanvas-content");
      const numItems = parseInt(
        liveDrawer?.getAttribute("data-cart-items-count") ??
          refreshedElement.getAttribute("data-cart-items-count") ??
          "0",
        10
      );
      const freshJson = parsedHtml.querySelector("#minicart-json");
      const liveJson = document.querySelector("#minicart-json");
      if (freshJson && liveJson) {
        liveJson.textContent = freshJson.textContent;
      }

      document.querySelectorAll("[data-cart-item-count]").forEach((el) => {
        el.setAttribute("data-cart-item-count", String(numItems));
        el.innerHTML = String(numItems);
      });

      const jsonSource = liveJson?.textContent ? liveJson.textContent : freshJson?.textContent;
      if (jsonSource) {
        const cartJSON = JSON.parse(jsonSource);
        document.dispatchEvent(new CustomEvent("minicart:rendered", { detail: { cart: cartJSON } }));
      }

      await CartDrawer.rebuildSwipersInDrawer(liveDrawer);

      if (open) {
        const panel = document.getElementById("cart-offcanvas");
        if (panel && window.bootstrap?.Offcanvas) {
          try {
            window.bootstrap.Offcanvas.getOrCreateInstance(panel).show();
          } catch {
            if (offcanvas && typeof offcanvas.show === "function") {
              offcanvas.show();
            }
          }
        } else if (offcanvas && typeof offcanvas.show === "function") {
          offcanvas.show();
        }
      }
    } finally {
      document.querySelectorAll(".cart-loading").forEach((e) => e.classList.add("d-none"));
    }
  }
}

export default CartDrawer;
